import jwtDecode from 'jwt-decode'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import log from 'loglevel'
import { RefreshTokenRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { AuthActionType } from '../context/authContext'
import { EnvConfig } from '../config/config'

export const NTRCE_API_URL = EnvConfig.ntrceApiUrl

// Parse all the formats of error that might come back from ntrced
const parseGrpcErrorMsg = (error) => {
  // Examples of the errors we see:
  // { code: 3, message: "rpc error: code = Unauthenticated desc = No such user.  [thumper@ntrce.co] }
  // { code: 16, message: "No such user.  []"
  try {
    // parse the "code = .. desc =" format
    let desc_match = error.message.match(/desc = (.*) {2}\[(.*)]$/)
    if (!desc_match) {
      // Otherwise, it must be the simpler format
      desc_match = error.message.match(/^(.*) {2}\[(.*)]$/)
    }
    const generic_desc = desc_match[1]
    const argument_desc = desc_match[2]

    // if there is no argument, or it looks like an email, then ignore the arg
    if (!argument_desc || argument_desc.includes('@')) {
      return generic_desc.split(';')
    }

    // otherwise, prefer to use the argument
    return argument_desc.split(';')
  } catch (exc) {
    return error.message
  }
}

export const translateAuthError = (error) => {
  const errorList = parseGrpcErrorMsg(error)
  const parsedErrors = {}
  switch (error.code) {
    case 2: // UNKNOWN
      throw new Error('Our service is currently unavailable.')
    case 3: // INVALID_ARGUMENT
      for (const singleError of errorList) {
        if (singleError.includes('Password')) {
          parsedErrors.password = singleError
        } else if (singleError.includes('Email')) {
          parsedErrors.email = singleError
        } else {
          parsedErrors.general = singleError
        }
      }
      return parsedErrors
    case 16: // AUTHENTICATION ERROR
      for (const singleError of errorList) {
        if (singleError.includes('No such user.')) {
          parsedErrors.email = singleError
        } else {
          parsedErrors.general = singleError
        }
      }
      return parsedErrors
    default:
      throw new Error(error.message)
  }
}

export const translateTokenError = (error) => {
  const errorList = parseGrpcErrorMsg(error)
  const parsedErrors = {}
  switch (error.code) {
    case 3: // INVALID_ARGUMENT
      for (const singleError of errorList) {
        if (singleError === "header authorization doesn't exist") {
          parsedErrors.token = singleError
        } else {
          parsedErrors.general = singleError
        }
      }
      return parsedErrors
    case 7: {
      // PERMISSION_DENIED
      // Normalize errorList to always be an array for consistent checking
      const errorArray = Array.isArray(errorList) ? errorList : [errorList]

      // Check for token version errors
      const hasTokenVersionError = errorArray.some(
        (ele) =>
          typeof ele === 'string' && ele.includes('Invalid token version'),
      )

      // Check for permissions version errors
      const hasPermsVersionError = errorArray.some(
        (ele) =>
          typeof ele === 'string' &&
          ele.includes('Invalid permissions version'),
      )

      if (hasTokenVersionError) {
        parsedErrors.token = 'Token needs to be updated'
        parsedErrors.shouldRefresh = true
      } else if (hasPermsVersionError) {
        parsedErrors.token = 'Permissions version needs to be updated'
        parsedErrors.shouldRefresh = true
      } else if (
        errorArray.some(
          (ele) => typeof ele === 'string' && ele.includes('readonly tokens'),
        )
      ) {
        showInfoToast('You do not have permission to perform this action')
        parsedErrors.void = true
        return parsedErrors
      } else {
        if (typeof errorList === 'string') {
          parsedErrors.general = errorList?.split(' ,')
        } else if (Array.isArray(errorList)) {
          parsedErrors.general = errorList
        }
      }
      return parsedErrors
    }
    case 16: // UNAUTHENTICATED
      return { token: error.message }
    default:
      // Re-throw the same error, otherwise we lose context from the original error
      throw error
  }
}

export const authHeader = (accessToken) => {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

/**
 * Attempts to refresh the token if it's invalid due to version mismatch.
 * Returns the new token if refresh succeeds, null otherwise.
 */
export const attemptTokenRefresh = async (authCtx, authDispatch) => {
  if (!authCtx?.accessToken || !authDispatch) {
    log.warn('attemptTokenRefresh: Missing authCtx.accessToken or authDispatch')
    return null
  }

  try {
    log.info('Attempting to refresh token due to version mismatch')
    // Create a client with the current (potentially invalid) token
    // The refresh endpoint should still work as it validates the token differently
    const client = new AccountPromiseClient(NTRCE_API_URL, null, null)
    const header = authHeader(authCtx.accessToken)
    const request = new RefreshTokenRequest()
    if (authCtx.accountId) {
      request.setAccountId(authCtx.accountId)
    }

    const response = await client.refreshToken(request, header)
    const newToken = response.getJwsToken()

    if (newToken) {
      log.info('Token refresh successful')
      const { auc: useCase, rol: role, aid: accountId } = jwtDecode(newToken)

      authDispatch({
        type: AuthActionType.REFRESH_TOKEN,
        accessToken: newToken,
        accountId,
        useCase: Number(useCase),
        role: Number(role),
      })

      return newToken
    } else {
      log.warn('Token refresh returned empty token')
    }
  } catch (refreshErr) {
    // Refresh failed, will fall through to logout
    log.warn('Token refresh failed:', refreshErr)
    if (refreshErr?.code) {
      log.warn(
        'Refresh error code:',
        refreshErr.code,
        'message:',
        refreshErr.message,
      )
    }
  }

  return null
}

export const interceptTokenErrors = async (authCtx, authDispatch, apiCall) => {
  const header = authHeader(authCtx.accessToken)
  try {
    return await apiCall(header)
  } catch (err) {
    const parsedErrors = translateTokenError(err, authDispatch)
    log.debug(
      'interceptTokenErrors: error code:',
      err?.code,
      'parsedErrors:',
      parsedErrors,
    )

    if (parsedErrors.token) {
      // If the error is due to token/permission version mismatch, try refreshing first
      if (parsedErrors.shouldRefresh) {
        log.info('Token/permission version error detected, attempting refresh')
        const newToken = await attemptTokenRefresh(authCtx, authDispatch)
        if (newToken) {
          log.info('Retrying original request with refreshed token')
          // Retry the original request with the new token
          try {
            const newHeader = authHeader(newToken)
            return await apiCall(newHeader)
          } catch (retryErr) {
            // Retry failed, fall through to logout
            log.warn('Request retry after token refresh failed:', retryErr)
          }
        } else {
          log.warn('Token refresh failed, will logout')
        }
      }
      // If refresh didn't work or wasn't applicable, logout
      log.info('Logging out due to token error')
      authDispatch({ type: AuthActionType.LOGOUT })
    }
    throw err
  }
}
